// src/components/views/CityView.js
import React, { useMemo, useState, useEffect } from 'react';
import CityStats from '../CityStats';
import DistrictProblemsChart from '../charts/DistrictProblemsChart';
import DistrictTable from '../DistrictTable';
import SurveyModal from '../SurveyModal';
import * as XLSX from 'xlsx';
import { 
  getCityStatistics,
  getDistrictData,
  hasTimelinessProblems,
  isDateOutdatedTmRk,
  isDateOutdatedcp,
  THRESHOLDS
} from '../../utils/calculations';

const CityView = ({ allData, filters, updateFilters, onLoadSurveyData }) => {
  const { dates, survey } = allData;
  const [showSurveyModal, setShowSurveyModal] = useState(false);
  const [surveyLoading, setSurveyLoading] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    let interval;
    if (surveyLoading) {
      setProgress(0);
      interval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 100) {
            clearInterval(interval);
            return 100;
          }
          return prev + (100 / 70);
        });
      }, 100);
    } else {
      setProgress(0);
    }
    return () => clearInterval(interval);
  }, [surveyLoading]);

  const statistics = useMemo(() => {
    return getCityStatistics(allData, filters);
  }, [allData, filters]);

  const districtData = useMemo(() => {
    return getDistrictData(allData, filters);
  }, [allData, filters]);

  const allSchoolIds = useMemo(() => {
    return dates.map(school => school.Код.toString());
  }, [dates]);

  const handleShowSurvey = async () => {
    if (allSchoolIds.length === 0) {
      console.error('Нет ID ОУ для загрузки данных опроса');
      return;
    }
    setSurveyLoading(true);
    try {
      await onLoadSurveyData('city', 'spb', 'Санкт-Петербург', allSchoolIds);
      setShowSurveyModal(true);
    } catch (error) {
      console.error('Ошибка загрузки данных опроса:', error);
    } finally {
      setSurveyLoading(false);
    }
  };

  const exportDistrictStatsToExcel = () => {
    const exportData = districtData.map(item => {
      return {
        'Район': item.district,
        'Всего ОУ': item.totalSchools,
        'ОУ с проблемами': item.problemSchools,
        '% проблемных ОУ': item.problemPercentage,
        'Устаревшее ТМ': item.problemsByType['Устаревшее ТМ'],
        'Устаревшее КП': item.problemsByType['Устаревшее КП'],
        'Устаревшее РК': item.problemsByType['Устаревшее РК'],
        'ОУ с ошибками в меню': item.problemsByType['Ошибки в меню'],
        'ОУ с низким соответствием': item.problemsByType['Низкое соответствие'],
        'ОУ с проблемами публикации': item.problemsByType['Проблемы публикации'],
        'ОУ с низкой своевременностью': item.problemsByType['Низкая своевременность'],
        'Всего проблем': Object.values(item.problemsByType).reduce((sum, count) => sum + count, 0)
      };
    });

    const totals = {
      'Район': 'ВСЕГО',
      'Всего ОУ': exportData.reduce((sum, item) => sum + item['Всего ОУ'], 0),
      'ОУ с проблемами': exportData.reduce((sum, item) => sum + item['ОУ с проблемами'], 0),
      '% проблемных ОУ': exportData.reduce((sum, item) => sum + item['Всего ОУ'], 0) > 0 ? 
        (exportData.reduce((sum, item) => sum + item['ОУ с проблемами'], 0) / exportData.reduce((sum, item) => sum + item['Всего ОУ'], 0) * 100).toFixed(1) : 0,
      'Устаревшее ТМ': exportData.reduce((sum, item) => sum + item['Устаревшее ТМ'], 0),
      'Устаревшее КП': exportData.reduce((sum, item) => sum + item['Устаревшее КП'], 0),
      'Устаревшее РК': exportData.reduce((sum, item) => sum + item['Устаревшее РК'], 0),
      'ОУ с ошибками в меню': exportData.reduce((sum, item) => sum + item['ОУ с ошибками в меню'], 0),
      'ОУ с низким соответствием': exportData.reduce((sum, item) => sum + item['ОУ с низким соответствием'], 0),
      'ОУ с проблемами публикации': exportData.reduce((sum, item) => sum + item['ОУ с проблемами публикации'], 0),
      'ОУ с низкой своевременностью': exportData.reduce((sum, item) => sum + item['ОУ с низкой своевременностью'], 0),
      'Всего проблем': exportData.reduce((sum, item) => sum + item['Всего проблем'], 0)
    };

    exportData.push(totals);
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Статистика по районам');
    const fileName = `Статистика_по_районам_СПб_${filters.month}_${filters.year}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  if (dates.length === 0) {
    return (
      <div id="city-view" className="view">
        <div className="level-indicator">Город</div>
        <h2 className="view-title">Санкт-Петербург</h2>
        <div className="no-data">Нет данных для выбранного периода</div>
      </div>
    );
  }

  return (
    <div id="city-view" className="view">
      <div className="level-indicator">Город</div>
      <h2 className="view-title">Санкт-Петербург</h2>
      
      <CityStats statistics={statistics} />
      
     

      {showSurveyModal && (
        <SurveyModal
          surveyData={survey.city}
          entityName="Санкт-Петербург"
          level="city"
          onClose={() => setShowSurveyModal(false)}
        />
      )}
      
      <div className="dashboard">
        <div className="card wide-card">
          <h3>Проблемы ОУ по районам (% от ОУ в районе)</h3>
          <div className="chart-container">
            <DistrictProblemsChart 
              allData={allData} 
              districtData={districtData}
            />
          </div>
        </div>

        <div className="card wide-card">
          <div className="card-header-with-export">
            <h3>Детализация по районам</h3>
            <button className="btn-export" onClick={exportDistrictStatsToExcel}>
              Экспорт в Excel
            </button>
          </div>
          <DistrictTable 
            districtData={districtData}
            onDistrictClick={(district) => updateFilters({ district, school: '' })}
            filters={filters}
            allData={allData} 
          />
        </div>
      </div>
    </div>
  );
};

export default CityView;